import { useMemo, type Dispatch, type SetStateAction } from "react";
import { AdsHelpHint } from "@/components/ads-assistant/AdsAssistantCollapsible";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { inputClass, primaryBtnClass } from "@/components/service/serviceShared";
import type { ServiceOfferingDraft } from "@/components/service/useServiceOfferingsCatalog";
import type { ServiceWorkQueue } from "@/lib/service/types";

export function ServiceQuotesOfferingForm(props: {
  draft: ServiceOfferingDraft;
  setDraft: Dispatch<SetStateAction<ServiceOfferingDraft>>;
  queues: ServiceWorkQueue[];
  queuesUnavailable: boolean;
  actionBusy: boolean;
  onSave: () => void | Promise<void>;
  onCancel?: () => void;
  hideQuotable?: boolean;
  /** Lock slug when editing an existing service. */
  slugReadOnly?: boolean;
  /** Hide the slug field — slug is derived from label on save. */
  hideSlugField?: boolean;
  /** Compact card layout for service setup (dev-sync eed92189). */
  compact?: boolean;
}) {
  const {
    draft,
    setDraft,
    queues,
    queuesUnavailable,
    actionBusy,
    onSave,
    onCancel,
    hideQuotable,
    slugReadOnly,
    hideSlugField = true,
    compact = false,
  } = props;

  const proposalFormatKey = useMemo(() => {
    const slug = draft.slug.trim();
    if (!slug) return null;
    return resolveManagerPresetFormatKey(slug);
  }, [draft.slug]);

  const originHint = draft.quotable_from_sales
    ? "When marked quotable, this service's slug becomes an Opportunity product type. Deals with that product type can originate service jobs from the Opportunity Engine."
    : "Mark as quotable to expose this service as an Opportunity product type so deals can originate service jobs.";

  const jobTypeHint =
    "New services default to the New service job type when seeded. Change only if this offering should land on a different board lane.";

  const labelInputClass = compact ? `${inputClass} max-w-md` : inputClass;
  const narrowInputClass = compact ? `${inputClass} max-w-[11rem]` : inputClass;

  return (
    <div className={compact ? "grid gap-3 sm:grid-cols-2" : "grid gap-2 sm:grid-cols-2"}>
      <div className={compact ? "sm:col-span-2 flex flex-wrap items-center gap-2" : "sm:col-span-2"}>
        <input
          className={labelInputClass}
          placeholder="Service name"
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
        />
        <AdsHelpHint label="Opportunity Engine linkage">
          {originHint}
          {proposalFormatKey ? (
            <>
              {" "}
              Proposal format key <code className="font-mono">{proposalFormatKey}</code> is derived from
              the slug — configure copy under the Proposals tab.
            </>
          ) : null}
        </AdsHelpHint>
      </div>
      {hideSlugField ? null : (
        <input
          className={inputClass}
          placeholder="Slug (Opportunity product_type tag)"
          value={draft.slug}
          readOnly={slugReadOnly}
          onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))}
        />
      )}
      <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-zinc-200 sm:col-span-2">
        <span className="inline-flex flex-wrap items-center gap-2">
          Default job type
          {queuesUnavailable ? null : (
            <AdsHelpHint label="Default job type">{jobTypeHint}</AdsHelpHint>
          )}
        </span>
        <select
          className={`${inputClass} mt-1 max-w-md`}
          value={draft.default_queue_id}
          onChange={(e) => setDraft((d) => ({ ...d, default_queue_id: e.target.value }))}
        >
          <option value="">No default job type</option>
          {queues.map((q) => (
            <option key={q.id} value={q.id}>
              {q.label}
            </option>
          ))}
        </select>
      </label>
      <input
        className={narrowInputClass}
        type="number"
        min={0}
        placeholder="Duration (min)"
        value={draft.default_duration_min}
        onChange={(e) => setDraft((d) => ({ ...d, default_duration_min: e.target.value }))}
      />
      <input
        className={narrowInputClass}
        type="number"
        min={0}
        step="0.01"
        placeholder="Base price ($)"
        value={draft.base_price_dollars}
        onChange={(e) => setDraft((d) => ({ ...d, base_price_dollars: e.target.value }))}
      />
      {queuesUnavailable ? (
        <p className="text-xs text-amber-700 dark:text-amber-200 sm:col-span-2">
          Job types unavailable until the Service department is enabled — you can still save the
          service and assign a job type later.
        </p>
      ) : null}
      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
        <input
          type="checkbox"
          checked={draft.is_advertised}
          onChange={(e) => setDraft((d) => ({ ...d, is_advertised: e.target.checked }))}
        />
        Advertised (marketing / ads catalog)
      </label>
      {hideQuotable ? null : (
        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={draft.quotable_from_sales}
            onChange={(e) => setDraft((d) => ({ ...d, quotable_from_sales: e.target.checked }))}
          />
          Quotable from sales (adds slug to Opportunity product types when saved)
        </label>
      )}
      <div className="flex flex-wrap gap-2 sm:col-span-2">
        <button
          type="button"
          className={primaryBtnClass}
          disabled={actionBusy}
          onClick={() => void onSave()}
        >
          {actionBusy ? "Saving…" : draft.id ? "Save service" : "Create service"}
        </button>
        {onCancel ? (
          <button
            type="button"
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
            disabled={actionBusy}
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </div>
  );
}
